import React, { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter as Router, Navigate, Routes, Route, useLocation, type Location } from 'react-router-dom'
import { useAppStore } from '@/store/appStore'
import { useBackButton } from '@/hooks/useBackButton'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { BrowserAuthRedirect } from '@/components/auth/BrowserAuthRedirect'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { RoleProtectedRoute } from '@/components/auth/RoleProtectedRoute'
import CopyToast from '@/components/CopyToast'
import Loader from '@/components/Loader'
import { InstallGuidePageSkeleton } from '@/components/installGuide/InstallGuidePageSkeleton'
import { UiFeedbackHost } from '@/components/ui/UiFeedbackHost'
import { MobileMenu } from '@/components/navigation/MobileMenu'
import { useTelegramInit } from '@/hooks/useTelegramInit'
import { useAuth } from '@/hooks/useAuth'
import { locale } from '@/locales'
import { appConfig } from '@/config/app'
import { resolveViteRouterBasename } from '@/utils/appBase'
import { isTelegramMiniApp } from '@/utils/runtime'
import { startWebVitalsMonitoring } from '@/utils/performance'
import apiClient from '@/services/apiClient'
import { useTelegramWebApp } from '@/types/telegram'

const LoginPage = lazy(() => import('@/pages/LoginPage'))
const SubscriptionsPage = lazy(() => import('@/pages/SubscriptionsPage').then((module) => ({ default: module.SubscriptionsPage })))
const BalancePage = lazy(() => import('@/pages/BalancePage').then((module) => ({ default: module.BalancePage })))
const BalanceHistoryPage = lazy(() => import('@/pages/BalanceHistoryPage').then((module) => ({ default: module.BalanceHistoryPage })))
const BalanceExpensesPage = lazy(() => import('@/pages/BalanceExpensesPage').then((module) => ({ default: module.BalanceExpensesPage })))
const BalanceIncomesPage = lazy(() => import('@/pages/BalanceIncomesPage').then((module) => ({ default: module.BalanceIncomesPage })))
const ReferralsPage = lazy(() => import('@/pages/ReferralsPage').then((module) => ({ default: module.ReferralsPage })))
const ReferralTotalEarningsModalPage = lazy(() => import('@/pages/ReferralTotalEarningsModalPage').then((module) => ({ default: module.ReferralTotalEarningsModalPage })))
const ReferralCommissionModalPage = lazy(() => import('@/pages/ReferralCommissionModalPage').then((module) => ({ default: module.ReferralCommissionModalPage })))
const ReferralsListPage = lazy(() => import('@/pages/ReferralsListPage').then((module) => ({ default: module.ReferralsListPage })))
const SelectedReferralModalPage = lazy(() => import('@/pages/SelectedReferralModalPage').then((module) => ({ default: module.SelectedReferralModalPage })))
const TopupPaymentFormPage = lazy(() => import('@/pages/TopupPaymentFormPage').then((module) => ({ default: module.TopupPaymentFormPage })))
const TrialActivationPaymentFormPage = lazy(() => import('@/pages/TrialActivationPaymentFormPage').then((module) => ({ default: module.TrialActivationPaymentFormPage })))
const PaymentPage = lazy(() => import('@/pages/PaymentPage').then((module) => ({ default: module.PaymentPage })))
const ProfilePage = lazy(() => import('@/pages/ProfilePage').then((module) => ({ default: module.ProfilePage })))
const AdminPanelPage = lazy(() => import('@/pages/AdminPanelPage').then((module) => ({ default: module.AdminPanelPage })))
const SupportPage = lazy(() => import('@/pages/SupportPage').then((module) => ({ default: module.SupportPage })))
const InstallGuidePage = lazy(() => import('@/pages/InstallGuidePage').then((module) => ({ default: module.InstallGuidePage })))
const HappRedirectPage = lazy(() => import('@/pages/HappRedirectPage').then((module) => ({ default: module.HappRedirectPage })))
const PayHandoffPage = lazy(() => import('@/pages/PayHandoffPage').then((module) => ({ default: module.PayHandoffPage })))
const SubscriptionDetailsPage = lazy(() => import('@/pages/SubscriptionDetailsPage').then((module) => ({ default: module.SubscriptionDetailsPage })))
const SubscriptionAutopaymentPage = lazy(() => import('@/pages/SubscriptionAutopaymentPage').then((module) => ({ default: module.SubscriptionAutopaymentPage })))
const SubscriptionDevicesPage = lazy(() => import('@/pages/SubscriptionDevicesPage').then((module) => ({ default: module.SubscriptionDevicesPage })))
const SubscriptionQrPage = lazy(() => import('@/pages/SubscriptionQrPage').then((module) => ({ default: module.SubscriptionQrPage })))
const ConfigCatalogPage = lazy(() => import('@/pages/ConfigCatalogPage'))
const PlanPage = lazy(() => import('@/pages/PlanPage').then((module) => ({ default: module.PlanPage })))
const LegalPage = lazy(() => import('@/pages/LegalPage').then((module) => ({ default: module.LegalPage })))
const MaintenancePage = lazy(() => import('@/pages/MaintenancePage').then((module) => ({ default: module.MaintenancePage })))

type MaintenanceState = {
  enabled: boolean
  message: string
  blocked: boolean
}

const RouteLoader: React.FC = () => {
  const uiColors = appConfig.ui.colors

  return (
    <div className={`min-h-screen ${uiColors.pageBg} flex flex-col items-center justify-center gap-4`}>
      <Loader size={40} />
      <p className={`${uiColors.textSecondary} text-sm`}>{locale.common.loading}</p>
    </div>
  )
}

const shouldShowPersistentMobileMenu = (pathname: string, isAuthenticated: boolean, maintenance: MaintenanceState): boolean => {
  if (!isAuthenticated) {
    return false
  }

  if (maintenance.enabled && maintenance.blocked) {
    return false
  }

  if (pathname === appConfig.routes.subscriptions) {
    return true
  }

  if (pathname === appConfig.routes.balance) {
    return true
  }

  if (pathname === appConfig.routes.balanceHistory) {
    return false
  }

  if (pathname === appConfig.routes.referralsCommission) {
    return false
  }

  if (pathname === appConfig.routes.referralsTotalEarnings) {
    return false
  }

  if (pathname === appConfig.routes.referralsList || pathname.startsWith(`${appConfig.routes.referralsList}/`)) {
    return false
  }

  if (pathname === appConfig.routes.referrals) {
    return true
  }

  if (pathname === appConfig.routes.profile || pathname === appConfig.routes.dashboard) {
    return true
  }

  if (pathname === appConfig.routes.configs) {
    return true
  }

  if (pathname.startsWith(`${appConfig.routes.planPrefix}/`)) {
    return false
  }

  return false
}

const PersistentMobileMenu: React.FC<{ maintenance: MaintenanceState }> = ({ maintenance }) => {
  const isAuthenticated = useAppStore((state) => state.isAuthenticated)
  const location = useLocation()
  const pathname = location.pathname

  if (!shouldShowPersistentMobileMenu(pathname, isAuthenticated, maintenance)) {
    return null
  }

  return <MobileMenu />
}

const AppRoutes = ({ maintenance }: { maintenance: MaintenanceState }) => {
  const isAuthenticated = useAppStore((state) => state.isAuthenticated)
  const location = useLocation()
  const routeState = location.state as { backgroundLocation?: Location } | null
  const backgroundLocation = routeState?.backgroundLocation
  useBackButton()

  if (maintenance.enabled && maintenance.blocked) {
    return <MaintenancePage message={maintenance.message} />
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<RouteLoader />}>
        <Routes location={backgroundLocation || location}>
          <Route
            path={appConfig.routes.root}
            element={
              isAuthenticated ? (
                <Navigate to={appConfig.routes.subscriptions} replace />
              ) : isTelegramMiniApp() ? (
                <LoginPage />
              ) : (
                <BrowserAuthRedirect pathname={appConfig.routes.subscriptions} />
              )
            }
          />
          <Route path={appConfig.routes.subscriptions} element={
            <ProtectedRoute>
              <SubscriptionsPage />
            </ProtectedRoute>
          } />
          <Route path={appConfig.routes.balance} element={
            <ProtectedRoute>
              <BalancePage />
            </ProtectedRoute>
          } />
          <Route path={appConfig.routes.balanceHistory} element={
            <ProtectedRoute>
              <BalanceHistoryPage />
            </ProtectedRoute>
          } />
          <Route path={appConfig.routes.balanceHistoryExpenses} element={
            <ProtectedRoute>
              <BalanceExpensesPage />
            </ProtectedRoute>
          } />
          <Route path={appConfig.routes.balanceHistoryIncomes} element={
            <ProtectedRoute>
              <BalanceIncomesPage />
            </ProtectedRoute>
          } />
          <Route path={appConfig.routes.referrals} element={
            <ProtectedRoute>
              <ReferralsPage />
            </ProtectedRoute>
          } />
          <Route path={appConfig.routes.referralsTotalEarnings} element={
            <ProtectedRoute>
              <ReferralTotalEarningsModalPage />
            </ProtectedRoute>
          } />
          <Route path={appConfig.routes.referralsCommission} element={
            <ProtectedRoute>
              <ReferralCommissionModalPage />
            </ProtectedRoute>
          } />
          <Route path={appConfig.routes.referralsList} element={
            <ProtectedRoute>
              <ReferralsListPage />
            </ProtectedRoute>
          } />
          <Route path={appConfig.routes.referralsListItemPattern} element={
            <ProtectedRoute>
              <SelectedReferralModalPage />
            </ProtectedRoute>
          } />
          <Route path={appConfig.routes.profile} element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } />

          {/* Protected Routes */}
          <Route path={appConfig.routes.payment} element={
            <ProtectedRoute>
              <PaymentPage />
            </ProtectedRoute>
          } />
          <Route path={appConfig.routes.configs} element={
            <ProtectedRoute>
              <ConfigCatalogPage />
            </ProtectedRoute>
          } />
          <Route path={appConfig.routes.planPattern} element={
            <ProtectedRoute>
              <PlanPage />
            </ProtectedRoute>
          } />
          <Route path={appConfig.routes.dashboard} element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } />
          <Route path={appConfig.routes.support} element={
            <ProtectedRoute>
              <SupportPage />
            </ProtectedRoute>
          } />
          <Route path={appConfig.routes.happRedirect} element={
            <Suspense fallback={<Loader />}>
              <HappRedirectPage />
            </Suspense>
          } />
          <Route path={appConfig.routes.payHandoff} element={
            <Suspense fallback={<Loader />}>
              <PayHandoffPage />
            </Suspense>
          } />
          <Route path={appConfig.routes.installGuide} element={
            <ProtectedRoute>
              <Suspense
                fallback={
                  <InstallGuidePageSkeleton
                    title={locale.installGuide.loading.title}
                    subtitle={locale.installGuide.loading.subtitle}
                    ariaLabel={locale.installGuide.loading.ariaLabel}
                  />
                }
              >
                <InstallGuidePage />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path={appConfig.routes.subscriptionDetailsPattern} element={
            <ProtectedRoute>
              <SubscriptionDetailsPage />
            </ProtectedRoute>
          } />
          <Route path={appConfig.routes.subscriptionAutopaymentPattern} element={
            <ProtectedRoute>
              <SubscriptionAutopaymentPage />
            </ProtectedRoute>
          } />
          <Route path={appConfig.routes.subscriptionDevicesPattern} element={
            <ProtectedRoute>
              <SubscriptionDevicesPage />
            </ProtectedRoute>
          } />
          <Route path={appConfig.routes.subscriptionQrPattern} element={
            <ProtectedRoute>
              <SubscriptionQrPage />
            </ProtectedRoute>
          } />
          <Route path={appConfig.routes.legalPattern} element={<LegalPage />} />
          <Route path={appConfig.routes.homeTrialActivationPaymentForm} element={
            <ProtectedRoute>
              <TrialActivationPaymentFormPage />
            </ProtectedRoute>
          } />

          <Route path={appConfig.routes.admin} element={
            <RoleProtectedRoute requiredUsername={appConfig.auth.adminUsername}>
              <AdminPanelPage />
            </RoleProtectedRoute>
          } />
        </Routes>

        {backgroundLocation ? (
          <Routes>
            <Route path={appConfig.routes.referralsTotalEarnings} element={
              <ProtectedRoute>
                <ReferralTotalEarningsModalPage />
              </ProtectedRoute>
            } />
            <Route path={appConfig.routes.referralsCommission} element={
              <ProtectedRoute>
                <ReferralCommissionModalPage />
              </ProtectedRoute>
            } />
            <Route path={appConfig.routes.referralsListItemPattern} element={
              <ProtectedRoute>
                <SelectedReferralModalPage />
              </ProtectedRoute>
            } />
            <Route path={appConfig.routes.balanceTopupPaymentForm} element={
              <ProtectedRoute>
                <TopupPaymentFormPage />
              </ProtectedRoute>
            } />
            <Route path={appConfig.routes.homeTrialActivationPaymentForm} element={
              <ProtectedRoute>
                <TrialActivationPaymentFormPage />
              </ProtectedRoute>
            } />
          </Routes>
        ) : null}
      </Suspense>
    </ErrorBoundary>
  )
}

export const App: React.FC = () => {
  const webApp = useTelegramWebApp()

  // Initialize Telegram Web App
  useTelegramInit()

  // Handle Authentication
  useAuth()

  useEffect(() => {
    startWebVitalsMonitoring()
  }, [])

  const [maintenance, setMaintenance] = React.useState<MaintenanceState>({ enabled: false, message: '', blocked: false })
  const [isMaintenanceLoading, setIsMaintenanceLoading] = React.useState(true)

  useEffect(() => {
    let isMounted = true

    const loadMaintenance = async () => {
      setIsMaintenanceLoading(true)
      try {
        const response = await apiClient.getMiniAppMaintenance(webApp?.initData)
        if (!isMounted) {
          return
        }

        setMaintenance({
          enabled: Boolean(response?.maintenance?.enabled),
          message: String(response?.maintenance?.message || ''),
          blocked: Boolean(response?.maintenance?.blocked),
        })
      } catch {
        if (!isMounted) {
          return
        }

        setMaintenance({ enabled: false, message: '', blocked: false })
      } finally {
        if (isMounted) {
          setIsMaintenanceLoading(false)
        }
      }
    }

    void loadMaintenance()

    return () => {
      isMounted = false
    }
  }, [webApp?.initData])

  if (isMaintenanceLoading) {
    return <RouteLoader />
  }

  return (
    <Router basename={resolveViteRouterBasename()}>
      <AppRoutes maintenance={maintenance} />
      <PersistentMobileMenu maintenance={maintenance} />
      <CopyToast />
      <UiFeedbackHost />
    </Router>
  )
}
